import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Grid,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as BrainIcon,
  Functions as FunctionIcon,
  Timeline as TimelineIcon,
  Token as TokenIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Code as CodeIcon,
  Speed as PerformanceIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useColorMode } from 'src/theme/ThemeProvider';

interface MessageMetadataDebugProps {
  message: any;
}

const MessageMetadataDebug: React.FC<MessageMetadataDebugProps> = ({ message }) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';

  const metadata = message.metadata || {};
  const consumption = metadata.consumption || {};
  const functionSummary = metadata.function_execution_summary || {};
  const functionResults = metadata.function_results || [];

  // Calculate total tokens
  const totalInputTokens: number = Object.values(consumption).reduce(
    (sum: number, step: any) => sum + (step?.input_tokens || 0), 0
  ) as number;
  const totalOutputTokens: number = Object.values(consumption).reduce(
    (sum: number, step: any) => sum + (step?.output_tokens || 0), 0
  ) as number;
  const totalTokens: number = totalInputTokens + totalOutputTokens;

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Step display configuration
  const stepConfig = {
    rebase_conversation_thread: { emoji: '🧠', label: 'Context Analysis', color: '#2196F3' },
    list_chosen_functions: { emoji: '📋', label: 'Function Selection', color: '#FF9800' },
    call_chosen_functions: { emoji: '⚡', label: 'Function Execution', color: '#4CAF50' },
    informative_message: { emoji: '📖', label: 'Informative Variant', color: '#9C27B0' },
    fomo_message: { emoji: '⏰', label: 'FOMO Variant', color: '#F44336' },
    empathetic_message: { emoji: '💝', label: 'Empathetic Variant', color: '#E91E63' },
    critical_message: { emoji: '🚨', label: 'Critical Variant', color: '#FF5722' },
    creative_message: { emoji: '🎨', label: 'Creative Variant', color: '#673AB7' },
    safety_check: { emoji: '🛡️', label: 'Safety Check', color: '#607D8B' },
    final_message_critic: { emoji: '👑', label: 'Final Selection', color: '#FFD700' },
  };

  if (!metadata || Object.keys(metadata).length === 0) {
    return null;
  }

  return (
    <Box sx={{ 
      mb: 2, 
      p: 2, 
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      borderRadius: 2,
      bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : alpha(theme.palette.primary.main, 0.05),
    }}>
      {/* Core Message Info - Always visible at top */}
      <Box sx={{ 
        mb: 2, 
        p: 1.5, 
        bgcolor: isDarkMode ? alpha(theme.palette.background.default, 0.5) : alpha(theme.palette.grey[100], 0.8),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Message ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: theme.typography.fontFamilyMonospace, wordBreak: 'break-all' }}>
              {message.id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body2">{message.status}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Created At</Typography>
            <Typography variant="body2">
              {message.created_at ? new Date(message.created_at).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Updated At</Typography>
            <Typography variant="body2">
              {message.updated_at ? new Date(message.updated_at).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          {message.delivered_at && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Delivered At</Typography>
              <Typography variant="body2">
                {new Date(message.delivered_at).toLocaleString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Header with summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
          <CodeIcon />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🔍 Message Debug Info
            <Chip 
              label={`${metadata.progress || 0}%`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            {metadata.step && (
              <Chip 
                label={metadata.step} 
                size="small" 
                color="secondary"
                variant="filled"
              />
            )}
          </Typography>
          {totalTokens > 0 && (
            <Typography variant="body2" color="text.secondary">
              💰 Total: {totalTokens.toLocaleString()} tokens 
              (📥 {totalInputTokens.toLocaleString()} in, 📤 {totalOutputTokens.toLocaleString()} out)
            </Typography>
          )}
        </Box>
      </Box>

      {/* Token Consumption Steps */}
      {Object.keys(consumption).length > 0 && (
        <Accordion 
          expanded={expanded === 'consumption'} 
          onChange={handleChange('consumption')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TokenIcon color="primary" />
              <Typography variant="subtitle1">
                Token Consumption ({Object.keys(consumption).length} steps)
              </Typography>
              <Chip label={`${totalTokens.toLocaleString()} total`} size="small" color="primary" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Step</TableCell>
                    <TableCell align="right">Input Tokens</TableCell>
                    <TableCell align="right">Output Tokens</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(consumption).map(([stepName, stepData]: [string, any]) => {
                    const config = stepConfig[stepName as keyof typeof stepConfig] || 
                      { emoji: '⚙️', label: stepName.replace(/_/g, ' '), color: '#666' };
                    const stepTotal = (stepData.input_tokens || 0) + (stepData.output_tokens || 0);
                    const percentage = totalTokens > 0 ? ((stepTotal / totalTokens) * 100).toFixed(1) : '0';
                    
                    return (
                      <TableRow key={stepName}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span style={{ fontSize: '16px' }}>{config.emoji}</span>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {config.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {(stepData.input_tokens || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {(stepData.output_tokens || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{stepTotal.toLocaleString()}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {percentage}%
                            <LinearProgress 
                              variant="determinate" 
                              value={parseFloat(percentage)} 
                              sx={{ 
                                width: 40, 
                                height: 4,
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: config.color
                                }
                              }} 
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Function Execution Results */}
      {(functionResults.length > 0 || Object.keys(functionSummary).length > 0) && (
        <Accordion 
          expanded={expanded === 'functions'} 
          onChange={handleChange('functions')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FunctionIcon color="secondary" />
              <Typography variant="subtitle1">
                Function Execution
              </Typography>
              {functionSummary.total_executed > 0 && (
                <Chip 
                  label={`${functionSummary.total_executed}/${functionSummary.total_suggested} executed`} 
                  size="small" 
                  color="secondary" 
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Summary stats */}
            {Object.keys(functionSummary).length > 0 && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {functionSummary.total_suggested || 0}
                    </Typography>
                    <Typography variant="caption">Suggested</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {functionSummary.successful || 0}
                    </Typography>
                    <Typography variant="caption">Successful</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="warning.main">
                      {functionSummary.skipped || 0}
                    </Typography>
                    <Typography variant="caption">Skipped</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="h6" color="error.main">
                      {functionSummary.failed || 0}
                    </Typography>
                    <Typography variant="caption">Failed</Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Individual function results */}
            {functionResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Function Details:</Typography>
                {functionResults.map((result: any, index: number) => {
                  const getStatusIcon = (status: string) => {
                    switch (status) {
                      case 'success': return <SuccessIcon color="success" />;
                      case 'error': return <ErrorIcon color="error" />;
                      case 'skipped': return <PendingIcon color="warning" />;
                      default: return <PendingIcon />;
                    }
                  };

                  const getStatusColor = (status: string) => {
                    switch (status) {
                      case 'success': return 'success';
                      case 'error': return 'error';
                      case 'skipped': return 'warning';
                      default: return 'default';
                    }
                  };

                  return (
                    <Paper 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}` 
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Badge 
                          badgeContent={index + 1} 
                          color="primary" 
                          sx={{ '& .MuiBadge-badge': { fontSize: '10px' } }}
                        >
                          {getStatusIcon(result.status)}
                        </Badge>
                        <Typography variant="subtitle2" sx={{ fontFamily: theme.typography.fontFamilyMonospace }}>
                          {result.function_name}
                        </Typography>
                        <Chip 
                          label={result.status} 
                          size="small" 
                          color={getStatusColor(result.status) as any}
                          variant="outlined"
                        />
                        {result.suggestion?.priority && (
                          <Chip 
                            label={`Priority: ${result.suggestion.priority}`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      {result.reason && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          {result.reason}
                        </Typography>
                      )}
                      
                      {result.response && result.status === 'success' && (
                        <Box sx={{ ml: 4, mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Response: {typeof result.response === 'string' 
                              ? result.response.substring(0, 100) + '...'
                              : JSON.stringify(result.response).substring(0, 100) + '...'
                            }
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Other Debug Info */}
      <Accordion 
        expanded={expanded === 'debug'} 
        onChange={handleChange('debug')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PerformanceIcon color="info" />
            <Typography variant="subtitle1">Debug Information</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>Additional Metadata:</Typography>
              {metadata?.failed_step && (
                <Typography variant="body2" color="error">
                  Failed Step: {metadata.failed_step}
                </Typography>
              )}
              {metadata?.error_message && (
                <Typography variant="body2" color="error">
                  Error: {metadata.error_message}
                </Typography>
              )}
              {metadata?.retries && (
                <Typography variant="body2">
                  Retries: {metadata.retries}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography variant="subtitle2" gutterBottom>Performance:</Typography>
              {functionSummary?.execution_time && (
                <Typography variant="body2">
                  Function Execution Time: {functionSummary.execution_time}s
                </Typography>
              )}
              {metadata?.step && (
                <Typography variant="body2">
                  Current Step: {metadata.step}
                </Typography>
              )}
              {metadata?.progress !== undefined && (
                <Typography variant="body2">
                  Progress: {metadata.progress}%
                </Typography>
              )}
              {/* display ALL metadata in a collapsible box with truncated values after 200chars */}
              <Accordion>
                <AccordionSummary>
                  <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.5, fontSize: '11px' }}>
                    {JSON.stringify(message.metadata).slice(0, 200)}...
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.5, fontSize: '11px' }}>
                    {JSON.stringify(message.metadata)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default MessageMetadataDebug; 
